import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import { OverflowRevealController } from "./overflow-reveal-controller";
import "./kuika-overflow-text.scss";

export interface KuikaOverflowTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

/**
 * A single-line label that reveals itself instead of ending in an ellipsis: when it does not fit it
 * is clipped with a gradient fade, and while its row is hovered or keyboard-focused the text slides
 * left to show its tail, holds there, and starts over.
 *
 * Put `kuika-overflow-text-host` on the row so the slide follows the whole row's hover (that is how
 * a row's hover-only buttons still trigger it). Without a host the label reacts to its own hover.
 *
 * Callers keep their own `title` attribute: the native tooltip is still the fallback for anyone who
 * cannot hover, and the designer's E2E selectors address rows by it.
 */
const KuikaOverflowText: React.FC<KuikaOverflowTextProps> = ({
  children,
  className,
  ...restProps
}: KuikaOverflowTextProps) => {
  const rootRef: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);
  const trackRef: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);

  useEffect((): (() => void) | undefined => {
    const root: HTMLSpanElement | null = rootRef.current;
    const track: HTMLSpanElement | null = trackRef.current;

    if (!root || !track) {
      return undefined;
    }

    const controller: OverflowRevealController = new OverflowRevealController(root, track);

    controller.attach();

    return (): void => {
      controller.detach();
    };
  }, []);

  return (
    <span ref={rootRef} className={classNames("kuika-overflow-text", className)} {...restProps}>
      <span ref={trackRef} className="kuika-overflow-text__track">
        {children}
      </span>
    </span>
  );
};

export default KuikaOverflowText;
